import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { DEFAULT_IMAGE_CAROUSEL, resolveImageCarouselColumnKeys } from '../utils/imageCarouselConfig';
import {
  mapContentToSlide,
  imageCarouselSupportsSensorType,
  slideIsInsideZone,
  filterSlidesByClass,
} from '../utils/imageCarouselSlides';
import { hexToRgba } from '../theme';
import { formatSensorTypeLabel } from '../utils/sensorTypeLabels';

/**
 * Zonas (polígonos) de los sensores presentes en el carrusel, pedidas al servidor con el token
 * de la sesión y cacheadas por sensorId (rara vez cambian). Solo se usa cuando "mostrar solo
 * dentro de zona" está activo y no hay columna manual configurada.
 */
function useSensorZones(sensorIds, enabled, getAuthHeaders) {
  const cacheRef = useRef(new Map());
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const pending = sensorIds.filter((id) => id && !cacheRef.current.has(id));
    if (!pending.length) return undefined;
    let cancelled = false;
    Promise.all(
      pending.map((id) =>
        axios
          .post(
            '/api/chart-builder/image-carousel/sensor-zones',
            { sensorId: id },
            { headers: typeof getAuthHeaders === 'function' ? getAuthHeaders() : undefined }
          )
          .then((res) => {
            cacheRef.current.set(id, Array.isArray(res?.data?.zones) ? res.data.zones : []);
          })
          .catch(() => {
            cacheRef.current.set(id, []);
          })
      )
    ).then(() => {
      if (!cancelled) setVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sensorIds.join('|')]);

  // Objeto nuevo solo cuando llega data nueva, para que sirva de dependencia estable en el
  // useMemo que filtra las diapositivas.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => Object.fromEntries(cacheRef.current), [version]);
}

/**
 * Caja de una detección sobre la imagen, en porcentaje del tamaño natural de la imagen ya
 * cargada. Centrada en frame.x/y, mismo criterio que el modo "center-middle" del visor forense
 * (DetectionCanvas.tsx), pero en % en vez de recalcular la relación tamaño mostrado/tamaño real
 * con JavaScript: como la imagen ocupa el 100% de su contenedor y la altura se ajusta sola, una
 * posición en % ya queda alineada sin medir nada en pantalla.
 */
function DetectionOverlayBox({ detection, naturalWidth, naturalHeight }) {
  if (!detection.box || !naturalWidth || !naturalHeight) return null;
  const left = (detection.box.left / naturalWidth) * 100;
  const top = (detection.box.top / naturalHeight) * 100;
  const width = (detection.box.width / naturalWidth) * 100;
  const height = (detection.box.height / naturalHeight) * 100;
  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${left}%`,
        top: `${top}%`,
        width: `${width}%`,
        height: `${height}%`,
        border: '2px solid',
        borderColor: detection.color,
        borderRadius: 0.5,
        boxShadow: '0 0 0 1px rgba(255,255,255,0.6) inset',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          bottom: -18,
          bgcolor: detection.color,
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          lineHeight: '16px',
          px: 0.5,
          borderRadius: 0.5,
          whiteSpace: 'nowrap',
        }}
      >
        {(() => {
          const primary = detection.value || detection.subject || detection.class || '';
          return (
            <>
              {primary || (detection.probability != null ? '' : '—')}
              {detection.probability != null
                ? `${primary ? ' ' : ''}${Math.round(detection.probability * 100)}%`
                : ''}
            </>
          );
        })()}
      </Box>
    </Box>
  );
}

/**
 * Miniatura recortada de una sola detección, igual que la ficha de patentes de VSaaS
 * (LicensePlateCropedV2 en forensic-reconstruction-ui): se dibuja la imagen completa en un
 * canvas del tamaño exacto de detection.box y desplazada por -box.left/-box.top, de forma que
 * solo quede visible esa región (sin pedir un recorte al servidor, box ya viene en píxeles
 * reales gracias a boundingBoxFromFrame).
 */
function CroppedDetectionThumbnail({ dataUrl, detection }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const box = detection.box;

  useEffect(() => {
    if (!dataUrl || !box) return undefined;
    let cancelled = false;
    setReady(false);
    const img = new window.Image();
    img.onload = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = Math.max(1, Math.round(box.width));
      canvas.height = Math.max(1, Math.round(box.height));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, -box.left, -box.top);
      setReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setReady(false);
    };
    img.src = dataUrl;
    return () => {
      cancelled = true;
    };
  }, [dataUrl, box?.left, box?.top, box?.width, box?.height]);

  if (!box) return null;

  const primary = detection.value || detection.subject || detection.class || '';
  const probText = detection.probability != null ? `${Math.round(detection.probability * 100)}%` : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, maxWidth: 160 }}>
      <Box
        sx={{
          width: 140,
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          border: '2px solid',
          borderColor: detection.color,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{ width: ready ? '100%' : 0, height: 'auto', display: 'block' }}
        />
      </Box>
      {(primary || probText) && (
        <Chip
          size="small"
          variant="outlined"
          label={`${primary || (probText ? '' : '—')}${probText ? `${primary ? ' · ' : ''}${probText}` : ''}`}
          sx={{
            bgcolor: hexToRgba(detection.color, 0.2),
            borderColor: detection.color,
            color: 'text.primary',
            fontWeight: 600,
            height: 20,
            maxWidth: '100%',
            '& .MuiChip-label': { px: 1, fontSize: 11 },
          }}
        />
      )}
    </Box>
  );
}

/**
 * Imagen de una diapositiva, pedida al servidor con el token del usuario (el token nunca llega
 * al navegador), con la caja de cada detección dibujada encima. Mientras resuelve o si falla,
 * muestra un marcador de posición con el nombre del archivo, para que el resto de la tarjeta
 * (metadata, detecciones) siga siendo útil.
 *
 * La petición no se dispara al montar, sino solo cuando la tarjeta entra en el área visible
 * (IntersectionObserver): en modo horizontal con muchas filas, montar todas las tarjetas a la
 * vez no debería disparar todas sus peticiones a la vez.
 *
 * Con cropMode activo, en vez de la imagen completa con cajas, se muestra una miniatura
 * recortada por detección (CroppedDetectionThumbnail), igual que la ficha de patentes.
 */
function CarouselSlideImage({ slide, getAuthHeaders, cacheRef, cropMode }) {
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [naturalSize, setNaturalSize] = useState(null);
  const resolveMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/image-carousel/resolve-image', body, { headers }),
  });

  const cacheKey = `${slide.sensorId || ''}|${slide.imageFile?.container || ''}|${slide.imageFile?.name || ''}`;

  useEffect(() => {
    if (inView) return undefined;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  useEffect(() => {
    if (!inView) return undefined;
    let cancelled = false;
    setNaturalSize(null);
    const cached = cacheRef?.current?.get(cacheKey);
    if (cached) {
      setDataUrl(cached);
      return undefined;
    }
    if (!slide.sensorId || !slide.imageFile?.container || !slide.imageFile?.name) {
      setFailed(true);
      return undefined;
    }
    resolveMutation
      .mutateAsync({
        body: { sensorId: slide.sensorId, ...slide.imageFile },
        headers: typeof getAuthHeaders === 'function' ? getAuthHeaders() : undefined,
      })
      .then((res) => {
        if (cancelled) return;
        const url = res?.data?.dataUrl;
        if (!url) {
          setFailed(true);
          return;
        }
        cacheRef?.current?.set(cacheKey, url);
        setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, inView]);

  if (dataUrl) {
    if (cropMode && slide.detections.length > 0) {
      return (
        <Box
          ref={containerRef}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1, justifyContent: 'center' }}
        >
          {slide.detections.map((d, di) => (
            <CroppedDetectionThumbnail key={di} dataUrl={dataUrl} detection={d} />
          ))}
        </Box>
      );
    }
    return (
      <Box ref={containerRef} sx={{ position: 'relative', width: '100%', lineHeight: 0 }}>
        <Box
          component="img"
          src={dataUrl}
          alt={slide.imageName || ''}
          onLoad={(e) => setNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
        {naturalSize &&
          slide.detections.map((d, di) => (
            <DetectionOverlayBox key={di} detection={d} naturalWidth={naturalSize.width} naturalHeight={naturalSize.height} />
          ))}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        minHeight: 140,
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', wordBreak: 'break-all' }}>
        {failed ? slide.imageName || 'No se pudo cargar la imagen' : 'Cargando imagen…'}
      </Typography>
    </Box>
  );
}

/**
 * Carrusel de imágenes de detecciones VSaaS. Cada diapositiva pide su imagen real al servidor
 * (con el token de la sesión), dibuja la caja de cada detección sobre la imagen y lista las
 * detecciones con su color real (paleta pastel definida en theme.js).
 */
export default function ImageCarouselWidget({ data = [], imageCarouselConfig = {}, getAuthHeaders }) {
  const rowsForCfg = Array.isArray(data) ? data : [];
  const cfg = resolveImageCarouselColumnKeys(
    { ...DEFAULT_IMAGE_CAROUSEL, ...imageCarouselConfig },
    rowsForCfg.find((r) => r && typeof r === 'object') || null
  );
  const [index, setIndex] = useState(0);
  const [autoPaused, setAutoPaused] = useState(false);
  const imageCacheRef = useRef(new Map());
  const filmstripScrollRef = useRef(null);
  const AUTO_ADVANCE_MS = 5000;

  // La columna manual (si está configurada) resuelve la zona sin pedir nada al servidor; sin
  // columna, se usa el polígono real del sensor, que sí requiere pedirlo (useSensorZones).
  const zoneFilterByPolygon = cfg.showOnlyInZone && !cfg.inZoneColumn;

  const { mappedSlides, rowCount, unsupportedTypes, hiddenByClassFilter } = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const withZoneFlag = rows.map((row) => ({
      row,
      inZone: cfg.inZoneColumn ? Boolean(row?.[cfg.inZoneColumn]) : true,
    }));
    const filtered = cfg.showOnlyInZone && cfg.inZoneColumn ? withZoneFlag.filter((r) => r.inZone) : withZoneFlag;
    const mapped = filtered
      .map(({ row }) =>
        mapContentToSlide(row?.[cfg.contentColumn], {
          sensorType: row?.[cfg.sensorTypeColumn],
          sensorId: row?.[cfg.sensorIdColumn],
        })
      )
      .filter(Boolean);
    // Los tipos de sensor sin imagen (o que todavía no soporta este gráfico) devuelven una fila
    // sin diapositiva; se listan aparte para no mostrar "no hay imágenes" sin explicar por qué.
    const types = new Set(
      filtered
        .map((r) => r.row?.[cfg.sensorTypeColumn])
        .filter((t) => t && !imageCarouselSupportsSensorType(t))
    );
    const withClassFilter = filterSlidesByClass(mapped, cfg.cropClassFilter);
    return {
      mappedSlides: withClassFilter,
      rowCount: filtered.length,
      unsupportedTypes: Array.from(types),
      // Hubo diapositivas reales (con imagen) pero el filtro de clase las dejó todas afuera:
      // distingue este caso del "no hay datos" genérico, para no hacer pensar que la query falló.
      hiddenByClassFilter: mapped.length > 0 && withClassFilter.length === 0,
    };
  }, [
    data,
    cfg.contentColumn,
    cfg.sensorTypeColumn,
    cfg.sensorIdColumn,
    cfg.inZoneColumn,
    cfg.showOnlyInZone,
    cfg.cropClassFilter,
  ]);

  const sensorIdsForZone = useMemo(() => {
    if (!zoneFilterByPolygon) return [];
    return Array.from(new Set(mappedSlides.map((s) => s.sensorId).filter(Boolean)));
  }, [mappedSlides, zoneFilterByPolygon]);

  const zonesBySensorId = useSensorZones(sensorIdsForZone, zoneFilterByPolygon, getAuthHeaders);

  const slides = useMemo(() => {
    if (!zoneFilterByPolygon) return mappedSlides;
    return mappedSlides.filter((s) => {
      const zones = s.sensorId ? zonesBySensorId[s.sensorId] : undefined;
      // Mientras la zona todavía no llega, no se oculta (evita parpadeo); una vez resuelta,
      // un sensor sin polígono tampoco se excluye (ver isInsideZone/slideIsInsideZone).
      if (zones === undefined) return true;
      return slideIsInsideZone(s, zones);
    });
  }, [mappedSlides, zoneFilterByPolygon, zonesBySensorId]);

  useEffect(() => {
    setIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (!cfg.singleImageMode || slides.length <= 1 || autoPaused) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [cfg.singleImageMode, slides.length, autoPaused]);

  const safeIndex = slides.length ? Math.min(index, slides.length - 1) : 0;

  useEffect(() => {
    if (cfg.singleImageMode || slides.length <= 1) return;
    const strip = filmstripScrollRef.current;
    if (!strip) return;
    const el = strip.querySelector(`[data-carousel-slide="${safeIndex}"]`);
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [cfg.singleImageMode, slides.length, safeIndex]);

  if (!slides.length) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {rowCount === 0
            ? 'No hay datos para mostrar.'
            : unsupportedTypes.length
              ? `No hay imágenes para mostrar: el tipo de sensor ${unsupportedTypes.map((t) => formatSensorTypeLabel(t)).join(', ')} todavía no está soportado por este gráfico.`
              : hiddenByClassFilter
                ? `Ninguna detección coincide con la clase "${cfg.cropClassFilter}" del filtro configurado.`
                : mappedSlides.length > 0 && zoneFilterByPolygon
                  ? 'No hay imágenes dentro de la zona configurada.'
                  : 'No hay imágenes para mostrar.'}
        </Typography>
      </Box>
    );
  }

  // Se acota una sola vez y se reutiliza para la diapositiva mostrada y para el contador, para
  // que no queden desincronizados si la cantidad de diapositivas cambia (p. ej. al cambiar un
  // filtro) mientras el índice guardado sigue apuntando a una posición que ya no existe.
  const currentIndex = Math.min(index, slides.length - 1);
  const filmstripMode = !cfg.singleImageMode;
  const visible = filmstripMode ? slides : [slides[currentIndex]];

  const goPrev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const goNext = () => setIndex((i) => (i + 1) % slides.length);

  const navButtonSx = {
    flexShrink: 0,
    alignSelf: 'center',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 1,
    '&:hover': { bgcolor: 'action.hover' },
  };

  const renderSlideCard = (slide, i) => (
    <Box
      {...(filmstripMode ? { 'data-carousel-slide': i } : {})}
      key={`${slide.sensorId || ''}-${slide.imageName || ''}-${i}`}
      sx={{
        flex: filmstripMode ? '0 0 220px' : '1 1 auto',
        minWidth: filmstripMode ? 220 : 0,
        border: '1px solid',
        borderColor: filmstripMode && i === currentIndex ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      <CarouselSlideImage slide={slide} getAuthHeaders={getAuthHeaders} cacheRef={imageCacheRef} cropMode={cfg.cropMode} />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatSensorTypeLabel(slide.sensorType)}
        </Typography>
        {/* En cropMode cada miniatura ya trae su propia etiqueta; repetirla aquí sería redundante. */}
        {!cfg.cropMode && slide.detections.map((d, di) => (
          <Chip
            key={di}
            size="small"
            variant="outlined"
            label={`${(d.value || d.subject || d.class || '') || (d.probability != null ? '' : '—')}${d.probability != null ? `${d.value || d.subject || d.class ? ' · ' : ''}${Math.round(d.probability * 100)}%` : ''}${d.direction ? ` · ${d.direction}` : ''}${d.stopped ? ' · detenido' : ''}${d.triggerSubject ? ' · objetivo' : ''}`}
            sx={{
              bgcolor: hexToRgba(d.color, 0.2),
              borderColor: d.color,
              color: 'text.primary',
              fontWeight: 600,
              height: 20,
              '& .MuiChip-label': { px: 1, fontSize: 11 },
            }}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}
      onMouseEnter={() => setAutoPaused(true)}
      onMouseLeave={() => setAutoPaused(false)}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch', gap: 1, minHeight: 0, pb: 0.5 }}>
        {slides.length > 1 && (
          <IconButton
            size="small"
            aria-label="Imagen anterior"
            onClick={goPrev}
            sx={navButtonSx}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        <Box
          ref={filmstripMode ? filmstripScrollRef : undefined}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'stretch',
            gap: 1.5,
            overflowX: filmstripMode ? 'auto' : 'hidden',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {visible.map((slide, i) => renderSlideCard(slide, filmstripMode ? i : currentIndex))}
        </Box>

        {slides.length > 1 && (
          <IconButton
            size="small"
            aria-label="Imagen siguiente"
            onClick={goNext}
            sx={navButtonSx}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {slides.length > 1 && !filmstripMode && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {currentIndex + 1} / {slides.length}
          {cfg.singleImageMode && !autoPaused ? ' · avance automático' : ''}
        </Typography>
      )}
    </Box>
  );
}
